import { ItemData } from 'store/types'
import DeleteIcon from 'atoms/icons/DeleteIcon'
import PostTextTools from 'organisms/post-tools/PostTextTools'
import PostImageTools from 'organisms/post-tools/PostImageTools'
import { useAtom } from 'jotai'
import { itemsDataAtom, selectedItemIdAtom } from 'store/atoms'
import { useMemo, useRef, useState } from 'react'
import DeletePostItemModal from 'molecules/DeletePostItemModal'
import ColorPickerButton from 'molecules/ColorPickerButton'
import PencilIcon from 'atoms/icons/PencilIcon'
import CornerIcon from 'atoms/icons/CornerIcon'
import SquareIcon from 'atoms/icons/SquareIcon'
import PostAddItemTools from 'organisms/post-tools/PostAddItemTools'
import ProductTools from 'organisms/post-tools/ProductTools'
import CancelIcon from 'atoms/icons/CancelIcon'
import PostShapeTools from 'organisms/post-tools/PostShapeTools'

function PostSelectedItemTools() {
  const [itemsData, setItemsData] = useAtom(itemsDataAtom)
  const [selectedItemId, setSelectedItemId] = useAtom(selectedItemIdAtom)

  const selectedItem = useMemo(
    () => (selectedItemId ? itemsData[selectedItemId] : undefined),
    [selectedItemId, itemsData]
  )

  const [borderWidthDropdownOpen, setBorderWidthDropdownOpen] = useState(false)
  const borderWidthRangeRef = useRef(null)

  const [cornerRadiusDropdownOpen, setCornerRadiusDropdownOpen] = useState(false)
  const cornerRadiusRangeRef = useRef(null)

  const [deletePostItemModalOpen, setDeletePostItemModalOpen] = useState(false)

  return (
    <>
      {selectedItem && (
        <div className='col-span-2 card bg-gray-100 outline-dashed outline-2 outline-black'>
          <div className='card-body p-4'>
            <div className='divider'>Container</div>
            <div className='flex flex-col gap-2'>
              <div className='flex flex-row flex-wrap gap-2'>
                <ColorPickerButton
                  leftElement={<PencilIcon size={18} />}
                  color={selectedItem.baseProps.borderColor}
                  onChangeColor={borderColor =>
                    setItemsData(oldState => ({
                      ...oldState,
                      [selectedItem.id]: {
                        ...(oldState[selectedItem.id] as ItemData<'text'>),
                        baseProps: {
                          ...selectedItem.baseProps,
                          borderColor
                        }
                      }
                    }))
                  }
                />
                <details
                  className='dropdown'
                  open={borderWidthDropdownOpen}
                  onClick={e => {
                    // prevent default on the Click Event to stop it conflicting
                    e.preventDefault()
                  }}>
                  <summary
                    className='btn btn-outline btn-sm'
                    onClick={() => setBorderWidthDropdownOpen(value => !value)}
                    onBlur={e => {
                      if (e.relatedTarget === borderWidthRangeRef.current) return
                      setBorderWidthDropdownOpen(false)
                    }}>
                    <SquareIcon size={18} />
                    <span>{selectedItem.baseProps.borderWidth}</span>
                  </summary>
                  <div className='menu dropdown-content bg-base-100 rounded-box z-[1] w-52 p-2 shadow'>
                    <input
                      ref={borderWidthRangeRef}
                      className='range input p-0'
                      type='range'
                      min='0'
                      max='20'
                      value={+selectedItem.baseProps.borderWidth.replace('%', '')}
                      onChange={e =>
                        setItemsData(oldState => ({
                          ...oldState,
                          [selectedItem.id]: {
                            ...(oldState[selectedItem.id] as ItemData<'text'>),
                            baseProps: {
                              ...selectedItem.baseProps,
                              borderWidth: e.target.value
                            }
                          }
                        }))
                      }
                      onBlur={() => setBorderWidthDropdownOpen(false)}
                    />
                  </div>
                </details>
                <details
                  className='dropdown'
                  open={cornerRadiusDropdownOpen}
                  onClick={e => {
                    // prevent default on the Click Event to stop it conflicting
                    e.preventDefault()
                  }}>
                  <summary
                    className='btn btn-outline btn-sm'
                    onClick={() => setCornerRadiusDropdownOpen(value => !value)}
                    onBlur={e => {
                      if (e.relatedTarget === cornerRadiusRangeRef.current) return
                      setCornerRadiusDropdownOpen(false)
                    }}>
                    <CornerIcon size={18} />
                    <span>{selectedItem.baseProps.borderRadius}</span>
                  </summary>
                  <div className='menu dropdown-content bg-base-100 rounded-box z-[1] w-52 p-2 shadow'>
                    <input
                      ref={cornerRadiusRangeRef}
                      className='range input p-0'
                      type='range'
                      min='0'
                      max='400'
                      value={selectedItem.baseProps.borderRadius}
                      onChange={e =>
                        setItemsData(oldState => ({
                          ...oldState,
                          [selectedItem.id]: {
                            ...(oldState[selectedItem.id] as ItemData<'text'>),
                            baseProps: {
                              ...selectedItem.baseProps,
                              borderRadius: +e.target.value
                            }
                          }
                        }))
                      }
                      onBlur={() => setCornerRadiusDropdownOpen(false)}
                    />
                  </div>
                </details>
              </div>
            </div>
            {(() => {
              switch (selectedItem.type) {
                case 'text':
                  return (
                    <>
                      <div className='divider'>Texto</div>
                      <PostTextTools
                        item={selectedItem}
                        onPropsChange={props => {
                          setItemsData(oldState => ({
                            ...oldState,
                            [selectedItem.id]: {
                              ...(oldState[selectedItem.id] as ItemData<'text'>),
                              props
                            }
                          }))
                        }}
                      />
                    </>
                  )
                case 'image':
                  return (
                    <>
                      <div className='divider'>Imagem</div>
                      <PostImageTools
                        item={selectedItem}
                        onPropsChange={props => {
                          setItemsData(oldState => ({
                            ...oldState,
                            [selectedItem.id]: {
                              ...(oldState[selectedItem.id] as ItemData<'image'>),
                              props
                            }
                          }))
                        }}
                      />
                    </>
                  )
                case 'shape':
                  return (
                    <>
                      <div className='divider'>Forma</div>
                      <PostShapeTools
                        item={selectedItem}
                        onPropsChange={props => {
                          setItemsData(oldState => ({
                            ...oldState,
                            [selectedItem.id]: {
                              ...(oldState[selectedItem.id] as ItemData<'shape'>),
                              props
                            }
                          }))
                        }}
                      />
                    </>
                  )
              }
            })()}
            <div className='divider' />
            <div className='flex flex-col gap-2'>
              <button className='btn btn-neutral' onClick={() => setSelectedItemId(undefined)}>
                <CancelIcon />
                Desselecionar
              </button>
              <button
                className='btn btn-outline btn-error'
                onClick={() => {
                  if (!selectedItem) return
                  setDeletePostItemModalOpen(true)
                }}>
                <DeleteIcon />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
      <PostAddItemTools />
      <ProductTools />
      <DeletePostItemModal
        itemName={
          selectedItem
            ? (() => {
                switch (selectedItem.type) {
                  case 'text':
                    return selectedItem.props.text
                  case 'image':
                    return selectedItem.props.name || 'Imagem vazia'
                  case 'shape':
                    return selectedItem.props.name
                }
              })()
            : ''
        }
        open={deletePostItemModalOpen}
        onClose={() => setDeletePostItemModalOpen(false)}
        onConfirm={() => {
          setDeletePostItemModalOpen(false)
          setItemsData(oldState => {
            if (!selectedItem) return oldState
            delete oldState[selectedItem.id]
            return oldState
          })
          setSelectedItemId(undefined)
        }}
      />
    </>
  )
}

export default PostSelectedItemTools
